import React, { useState, useEffect } from 'react'

const AboutMe = () => {
    const [aboutData, setAboutData] = useState(null);

    useEffect(() => {
        const fetchAboutData = async () => {
            try {
                const response = await fetch('/about-me.json');
                const data = await response.json();
                setAboutData(data);
            } catch (error) {
                console.error('Error loading about-me.json:', error);
            }
        };
        fetchAboutData();
    }, []);

    if (!aboutData) {
        return (
            <main className="py-16 sm:py-20 px-4 sm:px-8 md:px-12 bg-gradient-to-b from-white to-slate-50">
                <div className="max-w-7xl mx-auto text-center">
                    <p className="text-slate-600">Loading...</p>
                </div>
            </main>
        );
    }

    return (
        <main className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 md:px-8 bg-slate-50">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10 sm:mb-12">
                    <h1 id="about-me" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-3">
                        About Me
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mx-auto"></div>
                </div>

                <div className="space-y-8">
                    {/* General Info Card */}
                    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-slate-100">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xl">👤</span>
                            </div>
                            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">General Info</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Name</span>
                                <span className="text-base sm:text-lg text-slate-900">{aboutData.generalInfo.name}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Hometown</span>
                                <span className="text-base sm:text-lg text-slate-900">{aboutData.generalInfo.hometown}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Education</span>
                                <span className="text-base sm:text-lg text-slate-900">{aboutData.generalInfo.education}</span>
                            </div>
                            <div className="flex flex-col sm:col-span-2">
                                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Degree</span>
                                <span className="text-base sm:text-lg text-slate-900">{aboutData.generalInfo.degree}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-1">Expected Graduation</span>
                                <span className="text-base sm:text-lg text-slate-900">{aboutData.generalInfo.expectedGraduation}</span>
                            </div>
                        </div>
                    </div>

                    {/* Experience Section */}
                    {aboutData.experience && aboutData.experience.length > 0 && (
                        <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-slate-100">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-xl">💼</span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Experience</h2>
                            </div>
                            <div className="space-y-6">
                                {aboutData.experience.map((exp, idx) => (
                                    <div key={idx} className="flex gap-4 sm:gap-6 pb-6 border-b border-slate-100 last:border-b-0 last:pb-0">
                                        {/* Company Logo */}
                                        <div className="flex-shrink-0">
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-50 rounded-lg overflow-hidden border border-slate-200 flex items-center justify-center">
                                                <img
                                                    src={exp.logo}
                                                    alt={`${exp.company} logo`}
                                                    className="w-full h-full object-contain p-2"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.parentElement.innerHTML = '<span class="text-2xl text-slate-400">🏢</span>';
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {/* Experience Details */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">
                                                {exp.position}
                                            </h3>
                                            <p className="text-base sm:text-lg text-slate-700 font-medium mb-1">
                                                {exp.company}
                                            </p>
                                            <p className="text-sm text-slate-500 mb-3">
                                                {exp.duration}
                                            </p>
                                            <p className="text-base text-slate-600 leading-relaxed">
                                                {exp.description}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Hobbies and Organizations Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6">
                        {/* Hobbies Card */}
                        <div className="lg:col-span-3 xl:col-span-1 bg-white p-6 sm:p-7 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-slate-100">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <span className="text-white text-xl">🎯</span>
                            </div>
                            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Hobbies</h2>
                        </div>
                        <ul className="space-y-3">
                            {aboutData.hobbies.map((hobby, idx) => (
                                <li key={idx} className="flex items-start gap-3 group">
                                    <span className="text-purple-500 mt-0.5 group-hover:scale-110 transition-transform duration-200">•</span>
                                    <span className="text-base text-slate-700 group-hover:text-slate-900 transition-colors duration-200">{hobby}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                        {/* Organizations Card */}
                        <div className="lg:col-span-3 xl:col-span-2 bg-white p-6 sm:p-7 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-slate-100">
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-xl">🏛️</span>
                                </div>
                                <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Clubs & Organizations</h2>
                            </div>
                            <ul className="space-y-3">
                                {aboutData.organizations.map((org, idx) => (
                                    <li key={idx} className="flex items-start gap-3 group">
                                        <span className="text-indigo-500 mt-0.5 group-hover:scale-110 transition-transform duration-200">•</span>
                                        <span className="text-base text-slate-700 group-hover:text-slate-900 transition-colors duration-200">{org}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    )
}

export default AboutMe